import { axiosConfig } from '@/config/axios.config';
import { PaywallReason } from '@/types/paywall';
import { BrandAccountType, PlanLimit } from '@fluenceur/types';
import axios, { type InternalAxiosRequestConfig } from 'axios';
import { getSharedRefreshPromise } from './auth-guard';
import { getFreshAuthorizationHeader } from './fresh-authorization';
import { bypassManager } from './bypass-manager';
import {
  billingRedirectFor,
  captureBillingRequestScope,
  getBillingRequestScope,
  isStaleBillingResponse,
  readBillingAccess,
} from './billing-access';
import { hasShopifyIntegration } from './integration-checker';
import { tokenManager } from './token-manager';
import { getMcpReturnTo } from './mcp-return-to';
import { isPlanLimitCode, planLimitOfCode, readPlanLimitRefusal } from './plan-limit-refusal';
import { useUpgradeModalStore } from '@/stores/upgrade-modal-store';
import { analyticsSuppressionHeaders, signupAnalyticsHeaders } from './analytics-choice';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.fluenceur.local',
});

axiosInstance.defaults.maxRedirects = 0;

/**
 * Debounced login redirect to prevent race conditions.
 * If multiple 401s fire simultaneously (e.g. loader + parallel requests),
 * only one redirect is performed.
 */
let redirectTimeout: ReturnType<typeof setTimeout> | null = null;

function scheduleLoginRedirect() {
  if (redirectTimeout) return;
  redirectTimeout = setTimeout(() => {
    redirectTimeout = null;
    const continuation = getMcpReturnTo(`${window.location.pathname}${window.location.search}`);
    window.location.href = continuation
      ? `/?${new URLSearchParams({ returnTo: continuation })}`
      : '/';
  }, 100);
}

// Add a request interceptor to attach the Authorization header
axiosInstance.interceptors.request.use(
  async (config) => {
    captureBillingRequestScope(config);
    config.headers['x-product-analytics-surface'] = 'dashboard';
    if (!tokenManager.isAuthenticated())
      Object.assign(config.headers, signupAnalyticsHeaders(config.url));
    else Object.assign(config.headers, analyticsSuppressionHeaders());
    const authHeader = await getFreshAuthorizationHeader();

    if (authHeader) {
      config.headers.Authorization = authHeader;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Track retry status for each request to prevent infinite loops
const retryTracker = new WeakMap<InternalAxiosRequestConfig, boolean>();

// Add a response interceptor to handle errors and automatic token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const response = error.response;
    const config = error.config;

    // Handle 401 Unauthorized errors with automatic token refresh
    if (response && response.status === 401) {
      // Skip redirect for certain endpoints and pages
      const isLoginPage =
        window.location.pathname === '/' || window.location.pathname === '/signup';
      const isJoinTeamPage = window.location.pathname.startsWith('/team/join');
      const isAuthPage = isLoginPage || isJoinTeamPage;
      const isAuthEndpoint = config?.url?.includes('/auth/');
      const isMeEndpoint = config?.url?.includes('/users/me');

      // Check if this request has already been retried
      const hasRetried = retryTracker.get(config);

      // Don't attempt refresh if:
      // 1. This request has already been retried
      // 2. This is an auth page (login or team join)
      // 3. This is the refresh endpoint itself
      const shouldAttemptRefresh =
        !hasRetried &&
        !isAuthPage &&
        !config?.url?.includes('/auth/login') &&
        !config?.url?.includes('/auth/refresh');

      if (shouldAttemptRefresh) {
        console.log('[AXIOS] Received 401, attempting token refresh');

        // Mark this config as retried to prevent infinite loops
        retryTracker.set(config, true);

        const refreshResult = await getSharedRefreshPromise();

        if (refreshResult.success) {
          console.log('[AXIOS] Token refreshed successfully, retrying original request');

          // Get the new authorization header
          const newAuthHeader = tokenManager.getAuthorizationHeader();

          if (newAuthHeader) {
            // Update the config with the new token
            config.headers.Authorization = newAuthHeader;

            // Retry the original request
            return axiosInstance(config);
          }
        }

        // Token clearing is handled by refreshAccessToken for auth failures
        console.log('[AXIOS] Token refresh failed, scheduling redirect to login');
      }

      // Redirect to login using debounced redirect to prevent race conditions
      // with React Router loaders (which use throw redirect('/') from ensureValidToken)
      if (!isAuthPage && !isAuthEndpoint && !isMeEndpoint) {
        tokenManager.clearTokens();
        scheduleLoginRedirect();
      }
    }

    // Handle 423 Locked (success-fee terms unsigned past the deadline).
    //
    // Handled before the paywall branch and never routed into it: the merchant
    // is locked over a document, not over money, and sending it to the pricing
    // page would tell a paying customer to buy what it already has. The body
    // carries the instant the dashboard closed, so a page rendered straight off
    // this refusal can say since when.
    if (response && response.status === 423) {
      const deadline = response.data?.data?.deadline;

      import('@/stores/success-fee-lock-store').then(({ useSuccessFeeLockStore }) => {
        useSuccessFeeLockStore.getState().lock(typeof deadline === 'string' ? deadline : null);
      });

      return Promise.reject(error);
    }

    if (
      response?.status === 402 &&
      isStaleBillingResponse(config, response.data?.data ?? response.data)
    ) {
      return Promise.reject(error);
    }

    // Handle a 402 that names a limit of the brand's plan (searches, programmes,
    // Flows, connected assistants, recommendations).
    //
    // Handled before the subscription branch and never routed into it: the
    // brand is subscribed (or free) and simply reached what its plan includes.
    // Sending a paying Shopify merchant to the paywall would ask it to buy what
    // it already has; the upsell names the plan that lifts this one limit and
    // keeps a free way out. A plan code whose payload cannot be read still opens
    // the upsell of the limit its code names, built from the brand's own plan.
    if (response && response.status === 402 && isPlanLimitCode(response.data)) {
      const planLimit = readPlanLimitRefusal(response.data);
      const limit = planLimitOfCode(response.data);
      // The inviting organization owns this limit; the recipient cannot upgrade it.
      if (
        limit === PlanLimit.TEAM_MEMBERS &&
        /^\/team\/invitations\/[^/]+\/accept(?:\?|$)/.test(config?.url ?? '')
      ) {
        return Promise.reject(error);
      }
      if (planLimit)
        useUpgradeModalStore.getState().openPlanLimitModal(planLimit, getBillingRequestScope());
      else if (limit)
        useUpgradeModalStore.getState().openInferredPlanLimitModal(limit, getBillingRequestScope());

      return Promise.reject(error);
    }

    // Handle 402 Payment Required errors (subscription/paywall)
    // This works alongside SubscriptionGuard as a second layer of protection
    // The server decision wins. Older responses retain the historical fallback.
    if (response && response.status === 402) {
      const paywallData = response.data;
      // The reader itself is outside the subscription gate. A refusal is a read
      // failure, never a reason to recursively route its own request.
      if (config?.url === '/dashboard/billing/context') return Promise.reject(error);
      const billingAccess = readBillingAccess(
        paywallData.data?.billingAccess ?? paywallData.billingAccess
      );
      if (billingAccess) {
        if (!axiosConfig.shouldSkipPaywallRedirect(window.location.pathname)) {
          const destination = billingRedirectFor(
            billingAccess,
            window.location.pathname + (window.location.search ?? '')
          );
          if (destination) window.location.href = destination;
        }
        return Promise.reject(error);
      }

      // Determine the error type for the paywall page
      let errorType = 'subscription_required';
      let graceDays = 0;

      if (paywallData.subscription) {
        const status = paywallData.subscription.status;
        if (status === 'expired') {
          errorType = 'subscription_expired';
        } else if (status === 'cancelled') {
          errorType = 'subscription_expired';
        } else if (status === 'past_due') {
          errorType = 'payment_failed';
        }
      }

      if (paywallData.gracePeriodDaysRemaining) {
        graceDays = paywallData.gracePeriodDaysRemaining;
      }

      // For grace period, store data and show banner but don't redirect
      if (graceDays > 0) {
        // Import paywall store dynamically to avoid circular dependencies
        import('@/stores/paywall-store').then(({ usePaywallStore }) => {
          // Determine the reason for the paywall
          let reason: PaywallReason = PaywallReason.NO_SUBSCRIPTION;

          if (paywallData.subscription) {
            const status = paywallData.subscription.status;
            if (status === 'expired') {
              reason = PaywallReason.SUBSCRIPTION_EXPIRED;
            } else if (status === 'cancelled') {
              reason = PaywallReason.SUBSCRIPTION_CANCELLED;
            } else if (status === 'past_due') {
              reason = PaywallReason.PAYMENT_FAILED;
            }
          }

          // Update store with grace period info
          usePaywallStore.getState().showPaywall(paywallData, reason);
        });

        // Allow request to continue if in grace period
        if (paywallData.allowAccess) {
          return Promise.resolve(response);
        }
      } else {
        // A free organization is never sent to the paywall nor to the Shopify
        // onboarding: it has no subscription and owes no store.
        const isFreePlan = bypassManager.isOnFreePlan();

        // Hard block - check subscription bypass (internal/partner accounts)
        if (!isFreePlan && bypassManager.hasSubscriptionBypass()) {
          const accountType = bypassManager.getAccountType();
          // Partner accounts bypass everything including Shopify check
          if (accountType === BrandAccountType.PARTNER) {
            console.log('[AXIOS 402] Partner bypass active, skipping paywall redirect');
            return Promise.reject(error);
          }
          // Internal accounts still need Shopify integration
          const hasShopify = await hasShopifyIntegration();
          if (!hasShopify) {
            console.log('[AXIOS 402] Internal account without Shopify, redirecting to onboarding');
            window.location.href = '/onboarding/shopify';
            return Promise.reject(error);
          }
          console.log('[AXIOS 402] Subscription bypass active, skipping paywall redirect');
          return Promise.reject(error);
        }

        // Check if current path should skip automatic redirect
        // Configuration defined in @/config/axios.config.ts
        if (!axiosConfig.shouldSkipPaywallRedirect(window.location.pathname, { isFreePlan })) {
          // Priority 1: Check if Shopify integration exists (required for subscription)
          const hasShopify = await hasShopifyIntegration();

          if (!hasShopify) {
            // No Shopify integration - redirect to onboarding
            console.log('[AXIOS 402] No Shopify integration, redirecting to onboarding');
            window.location.href = '/onboarding/shopify';
          } else {
            // Has Shopify - redirect to paywall
            console.log('[AXIOS 402] Has Shopify, redirecting to paywall');
            const params = new URLSearchParams({
              type: errorType,
              ...(graceDays > 0 && { grace_days: graceDays.toString() }),
            });
            window.location.href = `/dashboard?paywall=plans&${params.toString()}`;
          }
        }
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
